import { Experiments } from '@wix/yoshi-flow-editor';
import { PaymentOption, PaymentType } from '../../../types/types';
import { SlotService, SlotServices } from '../../state/types';
import { mapToArray } from '../..';
import { ExperimentsConsts } from '../../../consts/experiments';

// TODO(Plan & Book): both assumptions below are still pending confirmation —
//   - eCom: a single cart can hold purchasable-plan items together with directly-paid
//     items and/or already-purchased-plan (USE_PLAN) items (a "mixed cart").
//   - Pricing Plans: a purchasable plan's additional-step (info-collection) URL can
//     carry more than one service/booking.
// Both are assumed `true` for now. Once each team confirms, flip the relevant flag —
// setting it to `false` re-enables the matching guard, so the appointment falls back to
// the legacy "Buy a plan" flow instead of "Buy plan & Book".
const MIXED_CARTS_SUPPORTED: boolean = true;
const ADDITIONAL_STEP_URL_SUPPORTS_MULTIPLE_SERVICES: boolean = true;

/**
 * Whether the whole appointment can go through the in-cart "Buy plan & Book" flow,
 * decided from the *currently selected* payment option of every service (the user can
 * toggle each service between paying directly and paying with a purchasable plan).
 *
 * Mirrors the design doc's multi-service rules: enable Plan & Book only when the selected
 * payment methods support it, otherwise fall back to the legacy "Buy a plan" flow.
 */
export const isPlanAndBookEligible = ({
  slotServices,
  experiments,
}: {
  slotServices: SlotServices;
  experiments: Experiments;
}): boolean => {
  if (!experiments.enabled(ExperimentsConsts.PlanAndBook)) {
    return false;
  }

  const selectedOptions = mapToArray<SlotService>(slotServices)
    .map((slotService) => slotService?.selectedPaymentOption)
    .filter((option): option is PaymentOption => Boolean(option));

  const buyPlanAndBookOptions = selectedOptions.filter(
    (option) => option.type === PaymentType.BUY_PLAN_AND_BOOK,
  );

  // Not a Plan & Book flow at all unless at least one service is paid by a purchasable plan.
  if (buyPlanAndBookOptions.length === 0) {
    return false;
  }

  // Mixed cart: a purchasable plan selected alongside a directly-paid item (SINGLE_SESSION)
  // or an already-purchased plan (USE_PLAN). eCom's cart can't currently hold these together.
  const hasMixedCartItem = selectedOptions.some(
    (option) =>
      option.type === PaymentType.SINGLE_SESSION ||
      option.type === PaymentType.USE_PLAN,
  );
  if (hasMixedCartItem && !MIXED_CARTS_SUPPORTED) {
    return false;
  }

  // Additional step: a purchasable plan that needs an info-collection step before purchase is
  // bought via the Pricing Plans additional-step page, not the eCom cart.
  const additionalStepPlanIds = new Set(
    buyPlanAndBookOptions
      .filter((option) => Boolean(option.purchasablePlanInfoCollectionUrl))
      .map((option) => option.purchasablePlanRef?.catalogItemId)
      .filter((catalogItemId): catalogItemId is string =>
        Boolean(catalogItemId),
      ),
  );

  // Hard rule (not flag-gated): each additional-step plan must be bought on its own Pricing
  // Plans page, which can only carry that one plan's purchase. So two or more distinct
  // additional-step plans can't be combined.
  if (additionalStepPlanIds.size > 1) {
    return false;
  }

  if (additionalStepPlanIds.size === 1) {
    const [additionalStepPlanId] = additionalStepPlanIds;
    // The additional-step page can carry a mix of bookings redeemed by that one plan and
    // bookings paid directly (SINGLE_SESSION) — PP forwards both as line items, only the
    // plan-covered ones carry a membershipPayment. What it can NOT carry is a booking
    // redeemed by a *different* plan (another purchasable plan or an already-owned one).
    const hasItemRedeemedByAnotherPlan = selectedOptions.some((option) => {
      if (option.type === PaymentType.SINGLE_SESSION) {
        return false;
      }
      return (
        option.type !== PaymentType.BUY_PLAN_AND_BOOK ||
        option.purchasablePlanRef?.catalogItemId !== additionalStepPlanId
      );
    });
    if (hasItemRedeemedByAnotherPlan) {
      return false;
    }

    // Multiple bookings carried through the same additional-step plan's URL (whether
    // plan-covered or paid directly) are only possible if the URL supports multiple.
    if (
      selectedOptions.length > 1 &&
      !ADDITIONAL_STEP_URL_SUPPORTS_MULTIPLE_SERVICES
    ) {
      return false;
    }
  }

  return true;
};

export const getSelectedServicesForBuyPlan = (
  slotServices: SlotServices,
): SlotService[] => {
  return mapToArray<SlotService>(slotServices).filter(
    (slotService) =>
      slotService?.selectedPaymentOption?.type === PaymentType.BUY_PLAN,
  );
};

export const getSelectedServicesForBuyPlanAndBook = (
  slotServices: SlotServices,
): SlotService[] => {
  return mapToArray<SlotService>(slotServices).filter(
    (slotService) =>
      slotService?.selectedPaymentOption?.type ===
      PaymentType.BUY_PLAN_AND_BOOK,
  );
};

/**
 * Whether the buyer chose to pay for at least one service with a purchasable plan.
 * Such a plan can't share the eCom cart with other items (see `isPlanAndBookEligible`)
 * and always submits through the Plan & Book flow regardless of which cta was pressed,
 * so callers use this to drop cart-specific ctas and labels.
 */
export const hasPurchasablePlanSelected = ({
  slotServices,
  experiments,
}: {
  slotServices: SlotServices;
  experiments: Experiments;
}): boolean =>
  experiments.enabled(ExperimentsConsts.PlanAndBook) &&
  getSelectedServicesForBuyPlanAndBook(slotServices).length > 0;

export const hasServicesWithoutAvailablePlans = (slotServices: SlotServices) =>
  getSelectedServicesForBuyPlan(slotServices).some(
    (slotService) => !slotService.eligiblePlans?.length,
  );

export const checkDifferentBuyPlans = (slotServices: SlotServices): boolean => {
  const selectedServicesForBuyPlan =
    getSelectedServicesForBuyPlan(slotServices);

  const plans = new Set(
    selectedServicesForBuyPlan.map(
      (slotService) => slotService.selectedPaymentOption.id,
    ),
  );

  return plans.size > 1;
};
